/**
 * MoodboardDesigner — UEIM-01
 * Full moodboard workspace: create, edit, version, approve, convert to scope.
 * Embedded in OpportunityDetail as the "Event Design" tab.
 */
import { useState, useEffect, useRef } from "react";
import {
  Plus, Save, GitBranch, CheckCircle2, XCircle, ChevronRight,
  Palette, Loader2, AlertCircle, Clock, Tag, Ruler, Pencil,
  Trash2, Upload, Image, X, Check, RotateCcw, Send,
  ShieldCheck, ArchiveIcon, ChevronDown,
} from "lucide-react";
import { moodboardApi } from "@/services/api";
import { useAuth } from "@/contexts/AuthContext";
import { hasCapability } from "@/services/api";
import ImageUpload from "@/components/ImageUpload";

// ── Palette ────────────────────────────────────────────────────────────────────
const C = {
  ivory:      "#FBF7F2",
  sand:       "#FAF6F0",
  gold:       "#C9A15F",
  ruby:       "#C64040",
  charcoal:   "#3A3330",
  slate:      "#7A706A",
  divider:    "rgba(201,161,95,0.15)",
  paleGreen:  { bg: "#E8F5EC", text: "#2E7D4F", border: "rgba(46,125,79,0.25)" },
  paleAmber:  { bg: "#FEF3C7", text: "#92400E", border: "rgba(146,64,14,0.20)" },
  paleBlue:   { bg: "#EEF4FF", text: "#0176D3", border: "rgba(1,118,211,0.20)" },
  paleGray:   { bg: "#F4F4F4", text: "#706E6B", border: "rgba(112,110,107,0.20)" },
  paleRed:    { bg: "#FEE2E2", text: "#C64040", border: "rgba(198,64,64,0.25)" },
};

// ── Status metadata ────────────────────────────────────────────────────────────
const MB_STATUS = {
  DRAFT:        { label: "Draft",          color: C.paleGray   },
  UNDER_REVIEW: { label: "Under Review",   color: C.paleAmber  },
  APPROVED:     { label: "Approved",       color: C.paleGreen  },
  ARCHIVED:     { label: "Archived",       color: C.paleGray   },
};
const EL_STATUS = {
  DRAFT:          { label: "Draft",           color: C.paleGray   },
  PENDING_REVIEW: { label: "Pending Review",  color: C.paleAmber  },
  APPROVED:       { label: "Approved",        color: C.paleGreen  },
  REJECTED:       { label: "Rejected",        color: C.paleRed    },
  SUPERSEDED:     { label: "Superseded",      color: C.paleGray   },
};

function StatusPill({ status, config }) {
  const conf = config?.[status] || C.paleGray;
  return (
    <span
      className="text-[10px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap"
      style={{ background: conf.color.bg, color: conf.color.text, border: `1px solid ${conf.color.border}` }}
    >
      {conf.label || status}
    </span>
  );
}

// ── Common element type suggestions (not canonical — just UX helpers) ──────────
const ELEMENT_TYPE_SUGGESTIONS = [
  "Entry Arch", "Main Stage", "Backdrop", "Flower Decor", "Podium",
  "Branding Standee", "Selfie Booth", "Exhibition Stall", "Pledge Wall",
  "Guest Chairs", "VIP Chairs", "Sound System", "Genset",
  "Balloon Decor", "Centre Piece", "Photo Booth", "Photo Backdrop",
  "Welcome Signage", "Truss Structure", "LED Wall",
];

// ── Add/Edit Element Form (modal) ──────────────────────────────────────────────
function ElementForm({ moodboardId, element, serviceCategories, onSave, onClose }) {
  const isEdit = Boolean(element);
  const [form, setForm] = useState({
    element_name:        element?.element_name || "",
    element_type:        element?.element_type || "",
    description:         element?.description || "",
    service_category_id: element?.service_category_id || "",
    quantity:            element?.quantity || "",
    unit:                element?.unit || "",
    dimensions:          element?.dimensions ? JSON.stringify(element.dimensions) : "",
    style_notes:         element?.style_notes || "",
    material_notes:      element?.material_notes || "",
    color_palette:       element?.color_palette || "",
  });
  const [saving,       setSaving]       = useState(false);
  const [showTypeSugg, setShowTypeSugg] = useState(false);
  const [showImgUpload, setShowImgUpload] = useState(false);
  const [savedElemId,   setSavedElemId] = useState(isEdit ? element.id : null);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!form.element_name.trim()) return;
    setSaving(true);
    try {
      let dims = undefined;
      if (form.dimensions.trim()) {
        try { dims = JSON.parse(form.dimensions); } catch { dims = undefined; }
      }
      const payload = {
        element_name:        form.element_name.trim(),
        element_type:        form.element_type || null,
        description:         form.description || null,
        service_category_id: form.service_category_id || null,
        quantity:            form.quantity ? parseFloat(form.quantity) : null,
        unit:                form.unit || null,
        dimensions:          dims,
        style_notes:         form.style_notes || null,
        material_notes:      form.material_notes || null,
        color_palette:       form.color_palette || null,
      };
      let result;
      if (isEdit) {
        result = await moodboardApi.updateElement(moodboardId, element.id, payload);
        setSavedElemId(element.id);
      } else {
        result = await moodboardApi.addElement(moodboardId, payload);
        setSavedElemId(result.data.id);
      }
      onSave(result.data);
      if (!isEdit) setShowImgUpload(true); // prompt image upload for new elements
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "w-full text-sm border rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-1 focus:ring-yellow-400";
  const inputStyle = { borderColor: C.divider };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-10 px-4 pb-10 overflow-y-auto"
      style={{ background: "rgba(58,51,48,0.55)", backdropFilter: "blur(4px)" }}
    >
      <div
        className="w-full max-w-lg rounded-2xl shadow-xl overflow-hidden"
        style={{ background: C.ivory, border: `1px solid ${C.divider}` }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4"
          style={{ borderBottom: `1px solid ${C.divider}` }}>
          <h3 className="font-semibold text-base" style={{ color: C.charcoal }}>
            {isEdit ? "Edit Design Element" : "Add Design Element"}
          </h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-stone-100">
            <X size={16} style={{ color: C.slate }} />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          {/* Element name */}
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide mb-1.5 block" style={{ color: C.slate }}>
              Element Name *
            </label>
            <input
              className={inputCls}
              style={inputStyle}
              placeholder="e.g. Main Stage, Entry Arch…"
              value={form.element_name}
              onChange={e => set("element_name", e.target.value)}
              data-testid="elem-name-input"
            />
          </div>

          {/* Element type with suggestions */}
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide mb-1.5 block" style={{ color: C.slate }}>
              Element Type <span className="font-normal text-stone-400">(optional)</span>
            </label>
            <div className="relative">
              <input
                className={inputCls}
                style={inputStyle}
                placeholder="MAIN_STAGE, ENTRY_ARCH, or free text…"
                value={form.element_type}
                onChange={e => set("element_type", e.target.value)}
                onFocus={() => setShowTypeSugg(true)}
                onBlur={() => setTimeout(() => setShowTypeSugg(false), 150)}
              />
              {showTypeSugg && (
                <div className="absolute z-10 top-full left-0 right-0 mt-1 rounded-lg shadow-lg border overflow-y-auto max-h-40"
                  style={{ background: C.ivory, borderColor: C.divider }}>
                  {ELEMENT_TYPE_SUGGESTIONS.filter(s => !form.element_type || s.toLowerCase().includes(form.element_type.toLowerCase())).map(s => (
                    <button key={s} className="w-full text-left px-3 py-1.5 text-xs hover:bg-stone-100"
                      style={{ color: C.charcoal }}
                      onMouseDown={() => set("element_type", s)}>
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Service category */}
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide mb-1.5 block" style={{ color: C.slate }}>
              Service Category <span className="font-normal text-stone-400">(optional)</span>
            </label>
            <select
              className={inputCls}
              style={inputStyle}
              value={form.service_category_id}
              onChange={e => set("service_category_id", e.target.value)}
              data-testid="elem-category-select"
            >
              <option value="">— Select category —</option>
              {serviceCategories.map(sc => (
                <option key={sc.id} value={sc.id}>{sc.name}</option>
              ))}
            </select>
          </div>

          {/* Quantity + Unit */}
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="text-xs font-semibold uppercase tracking-wide mb-1.5 block" style={{ color: C.slate }}>
                Quantity
              </label>
              <input
                className={inputCls}
                style={inputStyle}
                type="number"
                placeholder="e.g. 1, 200…"
                value={form.quantity}
                onChange={e => set("quantity", e.target.value)}
                data-testid="elem-qty-input"
              />
            </div>
            <div className="flex-1">
              <label className="text-xs font-semibold uppercase tracking-wide mb-1.5 block" style={{ color: C.slate }}>
                Unit
              </label>
              <input
                className={inputCls}
                style={inputStyle}
                placeholder="pcs, sqft, sets…"
                value={form.unit}
                onChange={e => set("unit", e.target.value)}
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide mb-1.5 block" style={{ color: C.slate }}>
              Description
            </label>
            <textarea
              className={`${inputCls} resize-none`}
              style={inputStyle}
              rows={2}
              placeholder="Brief description of the design element…"
              value={form.description}
              onChange={e => set("description", e.target.value)}
              data-testid="elem-desc-input"
            />
          </div>

          {/* Style notes */}
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide mb-1.5 block" style={{ color: C.slate }}>
              Style & Colour Notes
            </label>
            <textarea
              className={`${inputCls} resize-none`}
              style={inputStyle}
              rows={2}
              placeholder="Colour palette, theme, look & feel…"
              value={form.style_notes}
              onChange={e => set("style_notes", e.target.value)}
            />
          </div>

          {/* Material notes */}
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide mb-1.5 block" style={{ color: C.slate }}>
              Material Notes
            </label>
            <input
              className={inputCls}
              style={inputStyle}
              placeholder="e.g. Premium flex printing, MS pipe fabrication…"
              value={form.material_notes}
              onChange={e => set("material_notes", e.target.value)}
            />
          </div>

          {/* Image upload (only available after first save) */}
          {savedElemId && (
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide mb-1.5 block" style={{ color: C.slate }}>
                Reference Image
              </label>
              <ImageUpload
                moodboardId={moodboardId}
                elementId={savedElemId}
                existingImage={element?.primary_reference_image}
                onUploaded={() => {}}
              />
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 px-5 py-4" style={{ borderTop: `1px solid ${C.divider}` }}>
          <button
            onClick={onClose}
            className="text-sm px-4 py-2 rounded-lg font-medium"
            style={{ color: C.slate, border: `1px solid ${C.divider}` }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !form.element_name.trim()}
            className="text-sm px-4 py-2 rounded-lg font-medium flex items-center gap-1.5 transition-opacity"
            style={{ background: C.gold, color: "#fff", opacity: saving || !form.element_name.trim() ? 0.6 : 1 }}
            data-testid="elem-save-btn"
          >
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
            {isEdit ? "Save Changes" : "Add Element"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Design element card ────────────────────────────────────────────────────────
function ElementCard({ elem, serviceCategories, moodboardId, canManage, canReview, onUpdate, onDelete }) {
  const [acting, setActing] = useState(null);
  const [showEdit, setShowEdit] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [showReject, setShowReject] = useState(false);

  const sc = serviceCategories.find(s => s.id === elem.service_category_id);
  const status = EL_STATUS[elem.status] || { label: elem.status, color: C.paleGray };
  const imgSrc = elem.primary_reference_image?.storage_path
    ? moodboardApi.imageUrl(elem.primary_reference_image.storage_path)
    : null;

  const act = async (fn, key) => {
    setActing(key);
    try { onUpdate(await fn()); }
    catch {}
    finally { setActing(null); }
  };

  const isEditable = !["APPROVED", "SUPERSEDED", "DELETED"].includes(elem.status);

  return (
    <>
      <div
        className="rounded-2xl overflow-hidden flex flex-col transition-shadow hover:shadow-md"
        style={{ background: C.ivory, border: `1px solid ${C.divider}` }}
        data-testid={`design-element-card-${elem.id}`}
      >
        {/* Image area */}
        <div
          className="w-full aspect-video flex items-center justify-center relative overflow-hidden"
          style={{ background: C.sand, minHeight: "140px" }}
        >
          {imgSrc ? (
            <img src={imgSrc} alt={elem.primary_reference_image?.alt_text || elem.element_name}
              className="w-full h-full object-cover" />
          ) : (
            <div className="flex flex-col items-center gap-2 opacity-30">
              <Image size={28} style={{ color: C.slate }} />
              <span className="text-xs" style={{ color: C.slate }}>No reference image</span>
            </div>
          )}
          {/* Status badge overlay */}
          <div className="absolute top-2 right-2">
            <StatusPill status={elem.status} config={EL_STATUS} />
          </div>
        </div>

        <div className="p-3.5 flex-1 flex flex-col gap-2">
          {/* Name + type */}
          <div>
            <h4 className="font-semibold text-sm leading-snug" style={{ color: C.charcoal }}>
              {elem.element_name}
            </h4>
            {elem.element_type && (
              <p className="text-[11px] mt-0.5" style={{ color: C.slate }}>{elem.element_type}</p>
            )}
          </div>

          {/* Service category */}
          {sc && (
            <div className="flex items-center gap-1 text-[11px]" style={{ color: C.slate }}>
              <Tag size={10} /> {sc.name}
            </div>
          )}

          {/* Quantity + unit */}
          {elem.quantity && (
            <div className="flex items-center gap-1 text-[11px]" style={{ color: C.slate }}>
              <Ruler size={10} /> {elem.quantity} {elem.unit || ""}
            </div>
          )}

          {/* Description */}
          {elem.description && (
            <p className="text-[11px] leading-relaxed line-clamp-2" style={{ color: C.slate }}>
              {elem.description}
            </p>
          )}

          {/* Rejection reason */}
          {elem.status === "REJECTED" && elem.rejection_reason && (
            <div className="rounded px-2 py-1.5 text-[10px]"
              style={{ background: C.paleRed.bg, color: C.paleRed.text, border: `1px solid ${C.paleRed.border}` }}>
              Rejected: {elem.rejection_reason}
            </div>
          )}

          {/* Scope item link */}
          {elem.scope_item_id && (
            <div className="flex items-center gap-1 text-[10px]" style={{ color: C.paleGreen.text }}>
              <CheckCircle2 size={10} /> Scope item created
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center gap-1.5 mt-auto pt-1.5 flex-wrap">
            {canManage && isEditable && (
              <button
                onClick={() => setShowEdit(true)}
                className="flex items-center gap-1 text-[11px] px-2 py-1 rounded hover:bg-stone-100"
                style={{ color: C.slate, border: `1px solid ${C.divider}` }}
                data-testid={`edit-elem-${elem.id}`}
              >
                <Pencil size={10} /> Edit
              </button>
            )}
            {canManage && isEditable && elem.status === "DRAFT" && (
              <button
                onClick={() => act(() => moodboardApi.submitElement(moodboardId, elem.id).then(r => ({ ...elem, status: "PENDING_REVIEW" })), "submit")}
                disabled={acting === "submit"}
                className="flex items-center gap-1 text-[11px] px-2 py-1 rounded"
                style={{ background: C.paleAmber.bg, color: C.paleAmber.text, border: `1px solid ${C.paleAmber.border}` }}
                data-testid={`submit-elem-${elem.id}`}
              >
                {acting === "submit" ? <Loader2 size={10} className="animate-spin" /> : <Send size={10} />}
                Submit
              </button>
            )}
            {canReview && elem.status === "PENDING_REVIEW" && (
              <>
                <button
                  onClick={() => act(() => moodboardApi.approveElement(moodboardId, elem.id).then(() => ({ ...elem, status: "APPROVED" })), "approve")}
                  disabled={acting !== null}
                  className="flex items-center gap-1 text-[11px] px-2 py-1 rounded"
                  style={{ background: C.paleGreen.bg, color: C.paleGreen.text, border: `1px solid ${C.paleGreen.border}` }}
                  data-testid={`approve-elem-${elem.id}`}
                >
                  {acting === "approve" ? <Loader2 size={10} className="animate-spin" /> : <Check size={10} />}
                  Approve
                </button>
                <button
                  onClick={() => setShowReject(true)}
                  disabled={acting !== null}
                  className="flex items-center gap-1 text-[11px] px-2 py-1 rounded"
                  style={{ background: C.paleRed.bg, color: C.paleRed.text, border: `1px solid ${C.paleRed.border}` }}
                  data-testid={`reject-elem-${elem.id}`}
                >
                  <X size={10} /> Reject
                </button>
              </>
            )}
            {canManage && elem.status === "APPROVED" && !elem.scope_item_id && (
              <button
                onClick={() => act(() => moodboardApi.convertElementToScope(moodboardId, elem.id).then(r => ({ ...elem, scope_item_id: r.data.scope_item_id })), "convert")}
                disabled={acting === "convert"}
                className="flex items-center gap-1 text-[11px] px-2 py-1 rounded"
                style={{ background: C.paleBlue.bg, color: C.paleBlue.text, border: `1px solid ${C.paleBlue.border}` }}
                data-testid={`convert-elem-${elem.id}`}
              >
                {acting === "convert" ? <Loader2 size={10} className="animate-spin" /> : <ChevronRight size={10} />}
                Add to Scope
              </button>
            )}
            {canManage && isEditable && !elem.scope_item_id && (
              <button
                onClick={() => onDelete(elem.id)}
                className="ml-auto flex items-center gap-1 text-[11px] px-2 py-1 rounded hover:bg-stone-100"
                style={{ color: C.slate }}
                data-testid={`delete-elem-${elem.id}`}
              >
                <Trash2 size={10} />
              </button>
            )}
          </div>
        </div>
      </div>

      {showEdit && (
        <ElementForm
          moodboardId={moodboardId}
          element={elem}
          serviceCategories={serviceCategories}
          onSave={(updated) => { onUpdate(updated); setShowEdit(false); }}
          onClose={() => setShowEdit(false)}
        />
      )}

      {/* Reject reason modal */}
      {showReject && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4"
          style={{ background: "rgba(58,51,48,0.55)", backdropFilter: "blur(4px)" }}>
          <div className="w-full max-w-sm rounded-2xl p-5" style={{ background: C.ivory, border: `1px solid ${C.divider}` }}>
            <h4 className="font-semibold text-sm mb-3" style={{ color: C.charcoal }}>Reject Element</h4>
            <textarea
              className="w-full text-sm border rounded-lg px-3 py-2 resize-none focus:outline-none focus:ring-1 focus:ring-yellow-400"
              style={{ borderColor: C.divider }}
              rows={3}
              placeholder="Reason for rejection (optional)…"
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              data-testid="reject-reason-input"
            />
            <div className="flex justify-end gap-2 mt-3">
              <button onClick={() => setShowReject(false)} className="text-sm px-3 py-1.5 rounded" style={{ color: C.slate }}>
                Cancel
              </button>
              <button
                onClick={() => {
                  act(() => moodboardApi.rejectElement(moodboardId, elem.id, { reason: rejectReason }).then(() => ({ ...elem, status: "REJECTED", rejection_reason: rejectReason })), "reject");
                  setShowReject(false);
                }}
                className="text-sm px-3 py-1.5 rounded font-medium"
                style={{ background: C.ruby, color: "#fff" }}
                data-testid="confirm-reject-btn"
              >
                Reject
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ── Version history panel ──────────────────────────────────────────────────────
function VersionHistory({ moodboardId, onClose }) {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    moodboardApi.listVersions(moodboardId)
      .then(r => setVersions(r.data || []))
      .finally(() => setLoading(false));
  }, [moodboardId]);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-end"
      style={{ background: "rgba(58,51,48,0.40)" }}
      onClick={onClose}
    >
      <div
        className="h-full w-80 overflow-y-auto"
        style={{ background: C.ivory, borderLeft: `1px solid ${C.divider}` }}
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-4" style={{ borderBottom: `1px solid ${C.divider}` }}>
          <h3 className="font-semibold text-sm" style={{ color: C.charcoal }}>Version History</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-stone-100"><X size={14} /></button>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 size={16} className="animate-spin" style={{ color: C.slate }} />
          </div>
        ) : versions.length === 0 ? (
          <p className="text-xs p-4" style={{ color: C.slate }}>No versions saved yet.</p>
        ) : versions.map(v => (
          <div key={v.id} className="px-4 py-3 border-b" style={{ borderColor: C.divider }}>
            <div className="flex items-center gap-2">
              <span
                className="text-[11px] font-mono px-1.5 py-0.5 rounded font-medium"
                style={{ background: `${C.gold}18`, color: C.gold, border: `1px solid ${C.gold}30` }}
              >
                v{v.version_number}
              </span>
              <span className="text-xs font-medium" style={{ color: C.charcoal }}>
                {v.elements_count} element{v.elements_count !== 1 ? "s" : ""}
              </span>
            </div>
            <p className="text-[11px] mt-1" style={{ color: C.slate }}>
              {v.snapshot_by_name} · {new Date(v.snapshot_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Main MoodboardDesigner ─────────────────────────────────────────────────────
export default function MoodboardDesigner({ opportunityId, opportunityName }) {
  const { user } = useAuth();
  const canManage   = hasCapability(user, "moodboard.manage");
  const canReview   = hasCapability(user, "moodboard.review");
  const canRead     = hasCapability(user, "moodboard.read");

  const [moodboard,   setMoodboard]   = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [serviceCategories, setServiceCategories] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showVersions,setShowVersions]= useState(false);
  const [acting,      setActing]      = useState(null);
  const [createTitle, setCreateTitle] = useState(`${opportunityName || "Event"} — Design Board`);
  const [creating,    setCreating]    = useState(false);

  // Load moodboard + service categories
  useEffect(() => {
    if (!canRead) return;
    Promise.all([
      moodboardApi.list(opportunityId),
      moodboardApi.listServiceCategories(),
    ]).then(([mbRes, scRes]) => {
      const mbs = mbRes.data || [];
      setMoodboard(mbs[0] || null); // latest non-archived moodboard
      const scData = scRes.data;
      const cats = Array.isArray(scData) ? scData : scData?.categories || scData?.results || [];
      setServiceCategories(cats);
    }).finally(() => setLoading(false));
  }, [opportunityId, canRead]);

  const refreshMoodboard = async () => {
    if (!moodboard) return;
    const r = await moodboardApi.get(moodboard.id);
    setMoodboard(r.data);
  };

  const handleCreateMoodboard = async () => {
    if (!createTitle.trim()) return;
    setCreating(true);
    try {
      const r = await moodboardApi.create({ opportunity_id: opportunityId, title: createTitle.trim() });
      setMoodboard({ ...r.data, elements: [] });
    } finally {
      setCreating(false);
    }
  };

  const handleElementSaved = (updated) => {
    setMoodboard(prev => {
      if (!prev) return prev;
      const elems = prev.elements || [];
      const idx = elems.findIndex(e => e.id === updated.id);
      const newElems = idx >= 0
        ? elems.map((e, i) => i === idx ? updated : e)
        : [...elems, updated];
      return { ...prev, elements: newElems };
    });
  };

  const handleElementDelete = async (eid) => {
    try {
      await moodboardApi.deleteElement(moodboard.id, eid);
      setMoodboard(prev => ({
        ...prev,
        elements: (prev.elements || []).filter(e => e.id !== eid),
      }));
    } catch {}
  };

  const act = async (fn, key) => {
    setActing(key);
    try { await fn(); await refreshMoodboard(); }
    finally { setActing(null); }
  };

  const handleConvertAll = () =>
    act(async () => {
      await moodboardApi.convertAllToScope(moodboard.id);
    }, "convert-all");

  if (!canRead) return (
    <div className="p-8 text-center">
      <AlertCircle size={24} className="mx-auto mb-2 opacity-40" style={{ color: C.ruby }} />
      <p className="text-sm" style={{ color: C.slate }}>You do not have permission to view moodboards.</p>
    </div>
  );

  if (loading) return (
    <div className="flex items-center justify-center py-16">
      <Loader2 size={20} className="animate-spin" style={{ color: C.gold }} />
    </div>
  );

  // ── No moodboard yet ───────────────────────────────────────────────────────
  if (!moodboard) {
    if (!canManage) return (
      <div className="p-8 text-center">
        <Palette size={32} className="mx-auto mb-3 opacity-20" style={{ color: C.slate }} />
        <p className="text-sm" style={{ color: C.slate }}>No moodboard has been created for this opportunity yet.</p>
      </div>
    );
    return (
      <div className="p-8 flex flex-col items-center gap-4" data-testid="create-moodboard-section">
        <div
          className="w-full max-w-md rounded-2xl p-6"
          style={{ background: C.sand, border: `1px solid ${C.divider}` }}
        >
          <Palette size={28} className="mb-3" style={{ color: C.gold }} />
          <h3 className="font-semibold text-base mb-1" style={{ color: C.charcoal }}>Create Event Design Board</h3>
          <p className="text-xs mb-4" style={{ color: C.slate }}>
            Start building the visual design brief for this event. Add design elements, reference images, service categories, and quantities — then convert approved elements into Scope Items.
          </p>
          <input
            className="w-full text-sm border rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-1 focus:ring-yellow-400 mb-3"
            style={{ borderColor: C.divider }}
            placeholder="Moodboard title…"
            value={createTitle}
            onChange={e => setCreateTitle(e.target.value)}
            data-testid="moodboard-title-input"
          />
          <button
            onClick={handleCreateMoodboard}
            disabled={creating || !createTitle.trim()}
            className="flex items-center gap-2 text-sm px-4 py-2 rounded-xl font-medium transition-opacity"
            style={{ background: C.gold, color: "#fff", opacity: creating || !createTitle.trim() ? 0.6 : 1 }}
            data-testid="create-moodboard-btn"
          >
            {creating ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            Create Design Board
          </button>
        </div>
      </div>
    );
  }

  // ── Moodboard exists ────────────────────────────────────────────────────────
  const elements = moodboard.elements || [];
  const approvedCount  = elements.filter(e => e.status === "APPROVED").length;
  const pendingCount   = elements.filter(e => e.status === "PENDING_REVIEW").length;
  const convertedCount = elements.filter(e => e.scope_item_id).length;
  const mbStatus = MB_STATUS[moodboard.status] || { label: moodboard.status, color: C.paleGray };

  return (
    <div className="p-4 sm:p-6" data-testid="moodboard-designer">
      {/* Moodboard header */}
      <div
        className="rounded-2xl p-4 mb-5 flex items-start gap-3 flex-wrap"
        style={{ background: C.sand, border: `1px solid ${C.divider}` }}
        data-testid="moodboard-header"
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-base font-bold" style={{ color: C.charcoal }}>{moodboard.title}</h2>
            <span
              className="text-[11px] font-mono px-1.5 py-0.5 rounded font-medium"
              style={{ background: `${C.gold}18`, color: C.gold, border: `1px solid ${C.gold}30` }}
              data-testid="moodboard-version-badge"
            >
              v{moodboard.version_number}
            </span>
            <span
              className="text-[10px] px-2 py-0.5 rounded-full font-medium"
              style={{ background: mbStatus.color.bg, color: mbStatus.color.text, border: `1px solid ${mbStatus.color.border}` }}
              data-testid="moodboard-status-badge"
            >
              {mbStatus.label}
            </span>
          </div>
          {moodboard.description && (
            <p className="text-xs mt-1" style={{ color: C.slate }}>{moodboard.description}</p>
          )}
          <div className="flex items-center gap-3 mt-1.5 text-[11px]" style={{ color: C.slate }}>
            <span>{elements.length} element{elements.length !== 1 ? "s" : ""}</span>
            {approvedCount > 0 && <span style={{ color: C.paleGreen.text }}>{approvedCount} approved</span>}
            {pendingCount > 0 && <span style={{ color: C.paleAmber.text }}>{pendingCount} pending</span>}
            {convertedCount > 0 && <span style={{ color: C.paleBlue.text }}>{convertedCount} in scope</span>}
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-1.5 flex-wrap">
          <button
            onClick={() => setShowVersions(true)}
            className="flex items-center gap-1 text-[11px] px-2.5 py-1.5 rounded-lg"
            style={{ color: C.slate, border: `1px solid ${C.divider}` }}
            data-testid="show-versions-btn"
          >
            <GitBranch size={11} /> History
          </button>

          {canManage && (
            <button
              onClick={() => act(() => moodboardApi.createVersion(moodboard.id), "version")}
              disabled={acting !== null}
              className="flex items-center gap-1 text-[11px] px-2.5 py-1.5 rounded-lg"
              style={{ color: C.slate, border: `1px solid ${C.divider}` }}
              data-testid="create-version-btn"
            >
              {acting === "version" ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />}
              Save Version
            </button>
          )}

          {canManage && moodboard.status === "DRAFT" && (
            <button
              onClick={() => act(() => moodboardApi.submitReview(moodboard.id), "submit-review")}
              disabled={acting !== null || elements.length === 0}
              className="flex items-center gap-1 text-[11px] px-2.5 py-1.5 rounded-lg font-medium"
              style={{ background: C.paleAmber.bg, color: C.paleAmber.text, border: `1px solid ${C.paleAmber.border}`, opacity: elements.length === 0 ? 0.5 : 1 }}
              data-testid="submit-review-btn"
            >
              {acting === "submit-review" ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />}
              Submit for Review
            </button>
          )}

          {canReview && moodboard.status === "UNDER_REVIEW" && (
            <button
              onClick={() => act(() => moodboardApi.approve(moodboard.id), "approve-mb")}
              disabled={acting !== null}
              className="flex items-center gap-1 text-[11px] px-2.5 py-1.5 rounded-lg font-medium"
              style={{ background: C.paleGreen.bg, color: C.paleGreen.text, border: `1px solid ${C.paleGreen.border}` }}
              data-testid="approve-moodboard-btn"
            >
              {acting === "approve-mb" ? <Loader2 size={11} className="animate-spin" /> : <ShieldCheck size={11} />}
              Approve Moodboard
            </button>
          )}

          {canManage && approvedCount > 0 && approvedCount > convertedCount && (
            <button
              onClick={handleConvertAll}
              disabled={acting !== null}
              className="flex items-center gap-1 text-[11px] px-2.5 py-1.5 rounded-lg font-medium"
              style={{ background: C.paleBlue.bg, color: C.paleBlue.text, border: `1px solid ${C.paleBlue.border}` }}
              data-testid="convert-all-btn"
            >
              {acting === "convert-all" ? <Loader2 size={11} className="animate-spin" /> : <ChevronRight size={11} />}
              Convert All to Scope
            </button>
          )}
        </div>
      </div>

      {/* Elements grid */}
      {elements.length === 0 ? (
        <div className="text-center py-12 rounded-2xl" style={{ background: C.sand, border: `2px dashed ${C.divider}` }}>
          <Palette size={32} className="mx-auto mb-3 opacity-20" style={{ color: C.slate }} />
          <p className="text-sm font-medium mb-1" style={{ color: C.charcoal }}>No design elements yet</p>
          <p className="text-xs mb-4" style={{ color: C.slate }}>
            Add entry arches, main stage, backdrop, chairs, and other design elements to build the visual brief.
          </p>
          {canManage && (
            <button
              onClick={() => setShowAddForm(true)}
              className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-xl font-medium"
              style={{ background: C.gold, color: "#fff" }}
              data-testid="add-first-element-btn"
            >
              <Plus size={14} /> Add First Element
            </button>
          )}
        </div>
      ) : (
        <>
          {canManage && moodboard.status !== "APPROVED" && (
            <div className="flex justify-between items-center mb-4">
              <p className="text-xs" style={{ color: C.slate }}>{elements.length} design element{elements.length !== 1 ? "s" : ""}</p>
              <button
                onClick={() => setShowAddForm(true)}
                className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg font-medium"
                style={{ background: C.gold, color: "#fff" }}
                data-testid="add-element-btn"
              >
                <Plus size={12} /> Add Element
              </button>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {elements.map(elem => (
              <ElementCard
                key={elem.id}
                elem={elem}
                serviceCategories={serviceCategories}
                moodboardId={moodboard.id}
                canManage={canManage && moodboard.status !== "APPROVED"}
                canReview={canReview}
                onUpdate={handleElementSaved}
                onDelete={handleElementDelete}
              />
            ))}
          </div>
        </>
      )}

      {/* Add element modal */}
      {showAddForm && (
        <ElementForm
          moodboardId={moodboard.id}
          element={null}
          serviceCategories={serviceCategories}
          onSave={(elem) => { handleElementSaved(elem); setShowAddForm(false); }}
          onClose={() => setShowAddForm(false)}
        />
      )}

      {/* Version history panel */}
      {showVersions && (
        <VersionHistory moodboardId={moodboard.id} onClose={() => setShowVersions(false)} />
      )}
    </div>
  );
}
